import React, { useState } from 'react';
import { Form, Input, Button, DatePicker, Table, Card, message, Typography, Space, Progress, Tag } from 'antd';
import { MinusCircleOutlined, PlusOutlined, UploadOutlined, SaveOutlined } from '@ant-design/icons';
import { API_USER_URL } from "@/env";
import dayjs from 'dayjs';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';

const { Title, Text } = Typography;

interface AccountFormData {
    username: string; // Used for fullname
    email: string;
    password: string;
    dob: dayjs.Dayjs;
    ref_code?: string;
    otp: string;
}

interface ProcessResult {
    index: number;
    status: 'pending' | 'processing' | 'success' | 'error';
    message: string;
    step: string;
    error?: any;
}

const CreateAccountsPage: React.FC = () => {
    const [form] = Form.useForm();
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState<Record<number, ProcessResult>>({});

    const breadcrumbs: BreadcrumbItem[] = [
        { label: 'Admin', path: '/admin' },
        { label: 'Accounts', path: '/admin/accounts' },
        { label: 'Create Batch' }
    ];

    // --- Isolated API Functions to prevent Admin logout ---

    const apiSignUp = async (data: { email: string; password: string; referral_code?: string }) => {
        const payload = {
            email: data.email,
            password: data.password,
            password_confirmation: data.password, // Assuming confirm is same
            ...(data.referral_code ? { referral_code: data.referral_code } : {})
        };

        const res = await fetch(`${API_USER_URL}/api/v1/be/sign-up`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok || !json.status) {
            const error: any = new Error(json.message || 'Signup failed');
            error.errors = json.errors || json.error;
            throw error;
        }
        return {
            token: json.data.token,
            user: json.data.user,
            error: json.data.error
        };
    };

    const apiUpdateProfile = async (token: string, userId: string | number, data: { fullname: string; birthday: string }) => {
        const res = await fetch(`${API_USER_URL}/api/v1/be/users/${userId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok || (json.status === false)) {
            const error: any = new Error(json.message || 'Update profile failed');
            error.errors = json.errors || json.error;
            throw error;
        }
        return json.data;
    };

    const apiVerifyOtp = async (userId: string | number, otp: string) => {
        const res = await fetch(`${API_USER_URL}/api/v1/be/verify-email-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId, otp: otp }),
        });
        const json = await res.json();
        if (!res.ok || !json.status) {
            const error: any = new Error(json.message || 'OTP verification failed');
            error.errors = json.errors || json.error;
            throw error;
        }
        return true;
    };

    // --- Main Process Logic ---

    const processAccount = async (account: AccountFormData, index: number) => {
        try {
            // 1. Sign Up
            setResults(prev => ({
                ...prev,
                [index]: { index, status: 'processing', message: 'Signing up...', step: 'Signup' }
            }));

            const signUpRes = await apiSignUp({
                email: account.email,
                password: account.password,
                referral_code: account.ref_code
            });

            // 2. Verify OTP
            setResults(prev => ({
                ...prev,
                [index]: { ...prev[index], message: 'Verifying OTP...', step: 'Verify' }
            }));

            if (account.otp) {
                await apiVerifyOtp(signUpRes.user.id, account.otp);
            }

            // 3. Update Profile
            setResults(prev => ({
                ...prev,
                [index]: { ...prev[index], message: 'Updating profile...', step: 'Profile' }
            }));

            const dobFormatted = account.dob ? account.dob.format('YYYY-MM-DD') : '';
            await apiUpdateProfile(signUpRes.token, signUpRes.user.id, {
                fullname: account.username,
                birthday: dobFormatted
            });

            // Success
            setResults(prev => ({
                ...prev,
                [index]: { index, status: 'success', message: 'Completed successfully', step: 'Done' }
            }));

        } catch (error: any) {
            console.error("Batch processing error:", error);
            setResults(prev => ({
                ...prev,
                [index]: {
                    index,
                    status: 'error',
                    message: error.message || 'Unknown error',
                    step: 'Failed',
                    error: error
                }
            }));
        }
    };

    const onFinish = async (values: { users: AccountFormData[] }) => {
        if (!values.users || values.users.length === 0) {
            message.warning("Please add at least one account.");
            return;
        }

        setIsProcessing(true);
        // Initialize results state
        const initialResults: Record<number, ProcessResult> = {};
        values.users.forEach((_, idx) => {
            initialResults[idx] = { index: idx, status: 'pending', message: 'Pending', step: '-' };
        });
        setResults(initialResults);

        // Process sequentially to be safe with rate limits, or map with Promise.all for speed
        // Let's do sequential for better feedback
        for (let i = 0; i < values.users.length; i++) {
            await processAccount(values.users[i], i);
        }

        setIsProcessing(false);
        message.info("Batch processing completed. Check results below.");
    };

    return (
        <div className="p-6">
            <PageHeader title="Batch Create Accounts" breadcrumbs={breadcrumbs} />

            <Card className="shadow-md mb-6">
                <div className="mb-4">
                    <Text type="secondary">
                        Use this form to mass create user accounts. The process will:
                        1. Sign up user with Email & Password.
                        2. Update profile (Full Name, DOB) using the new user's token.
                        3. Verify the account using the provided OTP.
                        <br />
                        <strong>Note:</strong> This will NOT affect your current Admin session.
                    </Text>
                </div>

                <Form form={form} name="dynamic_form_nest_item" onFinish={onFinish} autoComplete="off" layout="vertical">
                    <Form.List name="users" initialValue={[{}]}>
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => {
                                    const result = results[index];
                                    let borderColor = undefined;
                                    if (result?.status === 'success') borderColor = '#52c41a';
                                    if (result?.status === 'error') borderColor = '#ff4d4f';
                                    if (result?.status === 'processing') borderColor = '#1890ff';

                                    return (
                                        <Card
                                            key={key}
                                            className="mb-4 bg-gray-50 border"
                                            style={{ borderColor: borderColor, borderWidth: result ? 2 : 1 }}
                                            size="small"
                                            title={<div className="flex flex-col gap-2">
                                                <Space>
                                                    <span>Account #{index + 1}</span>
                                                    {result && (
                                                        <Tag color={
                                                            result.status === 'success' ? 'success' :
                                                                result.status === 'error' ? 'error' :
                                                                    result.status === 'processing' ? 'processing' : 'default'
                                                        }>
                                                            {result.step}: {result.message}
                                                        </Tag>
                                                    )}
                                                </Space>
                                                {result?.status === 'error' && (
                                                    <div className="text-red-500 text-xs bg-red-50 p-2 rounded border border-red-100">
                                                        <strong>Details:</strong> {result.error?.errors ? JSON.stringify(result.error.errors) : (result.error?.message || JSON.stringify(result.error))}
                                                    </div>
                                                )}
                                            </div>}
                                            extra={
                                                <MinusCircleOutlined onClick={() => remove(name)} className="text-red-500 hover:text-red-700 cursor-pointer text-lg" />
                                            }
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'username']}
                                                    label="Full name"
                                                    rules={[{ required: true, message: 'Missing full name' }]}
                                                    className="mb-0"
                                                >
                                                    <Input placeholder="Full Name" />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'email']}
                                                    label="Email"
                                                    rules={[{ required: true, message: 'Missing email', type: 'email' }]}
                                                    className="mb-0"
                                                >
                                                    <Input placeholder="Email" />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'password']}
                                                    label="Password"
                                                    initialValue="123456789"
                                                    rules={[{ required: true, message: 'Missing password' }]}
                                                    className="mb-0"
                                                >
                                                    <Input.Password placeholder="Password" />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'dob']}
                                                    label="Date of Birth"
                                                    initialValue={dayjs(new Date())}
                                                    rules={[{ required: true, message: 'Missing DOB' }]}
                                                    className="mb-0 w-full"
                                                >
                                                    <DatePicker className="w-full" format="YYYY-MM-DD" />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'otp']}
                                                    label="Default OTP"
                                                    rules={[{ required: true, message: 'Missing OTP' }]}
                                                    initialValue={'123456'}
                                                    className="mb-0"
                                                >
                                                    <Input placeholder="123456" />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'ref_code']}
                                                    label="Referral Code"
                                                    className="mb-0"
                                                >
                                                    <Input placeholder="Optional" />
                                                </Form.Item>
                                            </div>
                                        </Card>
                                    )
                                })}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add Account Row
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={isProcessing} size="large" icon={<SaveOutlined />}>
                            {isProcessing ? 'Processing Batch...' : 'Create All Accounts'}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default CreateAccountsPage;
